import isEqual from 'lodash/isEqual';
import type {PayloadAction} from '@reduxjs/toolkit';
import {createSelector, createSlice} from '@reduxjs/toolkit';
import type {CollaborationParticipant, PresencePayload, RemoteSession, RemoteSessionsById} from '@PosterWhiteboard/collaboration/poster-presence.types';
import {pruneStaleSessions, removeRemoteSession, upsertRemoteSession} from '@PosterWhiteboard/collaboration/presence-sessions.library';
import {REMOTE_SESSION_STALE_MS} from '@PosterWhiteboard/collaboration/poster-presence.types';
import type {RootState} from '@/store';

/**
 * The other tabs editing the poster right now, as PosterPresence receives them. Written only
 * through PosterRedux; read by the nav-bar roster and the per-page cursor overlay.
 */
interface PosterPresenceState {
  remoteSessions: RemoteSessionsById;
}

interface RemoteSessionSeenPayload {
  payload: PresencePayload;
  participant: CollaborationParticipant;
  now: number;
}

interface RemoteSessionLeftPayload {
  sessionId: string;
  participant: CollaborationParticipant;
}

/** One person in the roster, with the colour of their first session so avatar and cursor match. */
export interface PresentParticipant extends CollaborationParticipant {
  color: string;
}

const initialState: PosterPresenceState = {
  remoteSessions: {},
};

const posterPresenceSlice = createSlice({
  name: 'posterPresence',
  initialState,
  reducers: {
    remoteSessionSeen: (state, action: PayloadAction<RemoteSessionSeenPayload>): void => {
      state.remoteSessions = upsertRemoteSession(state.remoteSessions, action.payload.payload, action.payload.participant, action.payload.now);
    },
    remoteSessionLeft: (state, action: PayloadAction<RemoteSessionLeftPayload>): void => {
      state.remoteSessions = removeRemoteSession(state.remoteSessions, action.payload.sessionId, action.payload.participant);
    },
    staleRemoteSessionsPruned: (state, action: PayloadAction<number>): void => {
      state.remoteSessions = pruneStaleSessions(state.remoteSessions, action.payload, REMOTE_SESSION_STALE_MS);
    },
    resetPosterPresence: (): PosterPresenceState => initialState,
  },
});

export const {remoteSessionSeen, remoteSessionLeft, staleRemoteSessionsPruned, resetPosterPresence} = posterPresenceSlice.actions;
export const posterPresenceReducer = posterPresenceSlice.reducer;

const selectRemoteSessions = (state: RootState): RemoteSessionsById => state.posterPresence.remoteSessions;

/** Every heartbeat rewrites the sessions, so the derived lists keep their reference while their content is unchanged. */
const areParticipantListsEqual = (previous: PresentParticipant[], next: PresentParticipant[]): boolean => {
  return previous.length === next.length && previous.every((participant, index) => isEqual(participant, next[index]));
};

const areStringListsEqual = (previous: string[], next: string[]): boolean => {
  return previous.length === next.length && previous.every((value, index) => value === next[index]);
};

export const selectPresentParticipants = createSelector(
  [selectRemoteSessions],
  (sessions): PresentParticipant[] => {
    const participantsByUserId = new Map<number, PresentParticipant>();
    for (const session of Object.values(sessions)) {
      if (!participantsByUserId.has(session.participant.userId)) {
        participantsByUserId.set(session.participant.userId, {...session.participant, color: session.color});
      }
    }
    return [...participantsByUserId.values()];
  },
  {memoizeOptions: {resultEqualityCheck: areParticipantListsEqual}}
);

/** Sessions grouped by the page they are on, so each page's overlay gets a reference that only changes when presence does. */
const selectRemoteSessionsByPage = createSelector([selectRemoteSessions], (sessions): ReadonlyMap<string, RemoteSession[]> => {
  const sessionsByPage = new Map<string, RemoteSession[]>();
  for (const session of Object.values(sessions)) {
    if (session.pageId !== null) {
      sessionsByPage.set(session.pageId, [...(sessionsByPage.get(session.pageId) ?? []), session]);
    }
  }
  return sessionsByPage;
});

export const selectPageIdsWithRemoteSessions = createSelector([selectRemoteSessionsByPage], (sessionsByPage): string[] => [...sessionsByPage.keys()], {
  memoizeOptions: {resultEqualityCheck: areStringListsEqual},
});

const NO_SESSIONS: RemoteSession[] = [];

export const selectRemoteSessionsOnPage = (state: RootState, pageId: string): RemoteSession[] => selectRemoteSessionsByPage(state).get(pageId) ?? NO_SESSIONS;
